import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
  CountryCode,
} from 'libphonenumber-js';
import { SelectProps } from '@amzn/awsui-components-react';

const DEFAULT_COUNTRY: CountryCode = 'US';
const ALL_ZEROS = /^0+$/;

const VALID_CALLING_CODES: ReadonlySet<string> = new Set(
  getCountries().map((country) => getCountryCallingCode(country))
);

// Preferred country for calling codes shared by multiple countries.
// Without this, the alphabetical winner from getCountries() is picked,
// which is rarely what users expect (e.g. AG over US for +1).
const PRIMARY_COUNTRY_BY_CALLING_CODE: Readonly<Record<string, CountryCode>> = {
  '1': 'US',
  '7': 'RU',
  '39': 'IT',
  '44': 'GB',
  '47': 'NO',
  '61': 'AU',
  '212': 'MA',
  '262': 'RE',
  '290': 'SH',
  '358': 'FI',
  '590': 'GP',
  '599': 'CW',
};

const COUNTRY_BY_CALLING_CODE: ReadonlyMap<string, CountryCode> = (() => {
  const map = new Map<string, CountryCode>();
  for (const country of getCountries()) {
    const cc = getCountryCallingCode(country);
    if (!map.has(cc)) map.set(cc, country);
  }
  for (const [cc, country] of Object.entries(PRIMARY_COUNTRY_BY_CALLING_CODE)) {
    map.set(cc, country);
  }
  return map;
})();

/**
 * True for all-zero legacy data and the +cc0000000 placeholder written by
 * formatPhoneForStorage for empty input. The calling-code check keeps real
 * numbers like +110000000 from being mistaken for a sentinel.
 */
function isSentinelPhone(phone: string): boolean {
  if (!phone) return false;
  const digits = phone.replace(/\D/g, '');
  if (ALL_ZEROS.test(digits)) return true;
  if (phone.startsWith('+')) {
    const rest = phone.slice(1);
    for (let len = 1; len <= 3 && len < rest.length; len++) {
      const prefix = rest.slice(0, len);
      const tail = rest.slice(len);
      if (VALID_CALLING_CODES.has(prefix) && ALL_ZEROS.test(tail) && tail.length >= 7) {
        return true;
      }
    }
  }
  return false;
}

export function buildCountryCodeOptions(): SelectProps.Options {
  return getCountries()
    .map((country) => ({
      value: country,
      label: `${country} (+${getCountryCallingCode(country)})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * First-mount guess for the country Select, based on the calling-code
 * prefix. The user can correct via the dropdown. Works for any input
 * length (parsePhoneNumber throws on inputs over ~15 digits, but our
 * phone field accepts up to 32 chars).
 */
export function inferCountry(phone: string): CountryCode {
  if (!phone || !phone.startsWith('+')) return DEFAULT_COUNTRY;
  const rest = phone.slice(1);
  for (let len = Math.min(3, rest.length); len >= 1; len--) {
    const country = COUNTRY_BY_CALLING_CODE.get(rest.slice(0, len));
    if (country) return country;
  }
  return DEFAULT_COUNTRY;
}

/**
 * Returns the digits after +callingCode for display in the editor input.
 * We avoid parsePhoneNumber here because its country-specific normalization
 * (e.g. stripping the NANP trunk 1) silently drops digits the user typed.
 */
export function getNationalDigits(phone: string, country: CountryCode): string {
  if (!phone || isSentinelPhone(phone)) return '';
  if (!phone.startsWith('+')) {
    return phone.replace(/\D/g, '');
  }
  const callingCode = getCountryCallingCode(country);
  if (phone.startsWith(`+${callingCode}`)) {
    return phone.slice(callingCode.length + 1);
  }
  // Stored prefix doesn't match the dropdown. Return everything after the
  // plus sign so digits aren't lost; the next blur rewrites storage with
  // the selected country's code.
  return phone.slice(1);
}

export function formatPhoneForStorage(country: CountryCode, nationalDigits: string): string {
  const callingCode = getCountryCallingCode(country);
  return nationalDigits ? `+${callingCode}${nationalDigits}` : `+${callingCode}0000000`;
}

/**
 * Formats a saved phone for the event details page. Uses the library's
 * grouping when it keeps every saved digit; otherwise shows
 * +countryCode rest so the details page matches the editor.
 */
export function formatPhoneForDisplay(phone: string): string {
  if (!phone || isSentinelPhone(phone)) return '';
  if (!phone.startsWith('+')) {
    const digits = phone.replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  try {
    const parsed = parsePhoneNumber(phone);
    const callingCode = parsed.countryCallingCode;
    const lexicalRemainder = phone.slice(callingCode.length + 1);
    if (parsed.nationalNumber !== lexicalRemainder) {
      return `+${callingCode} ${lexicalRemainder}`;
    }
    return parsed.formatInternational();
  } catch {
    return phone;
  }
}
